var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useLayoutEffect, useMemo, useState, } from "react";
import { Transforms } from "slate";
import { Editable, Slate, } from "slate-react";
import ComponentProvider from "../ComponentProvider";
import useDecorate from "../decorate";
import ElementRenderer from "../ElementRenderer";
import LeafRenderer from "../LeafRenderer";
import useOverrides from "../overrides";
import Placeholder from "../Placeholder";
import PluginProvider from "../PluginProvider";
import { useDeserialize, useSerialize } from "../serialize";
import useEventHandler from "../useEventHandler";
import useFluentEditInternal from "../useFluentEditInternal";
import { addRoot, editorToDomNode, focusEditor, isParagraph, removeRoot, } from "../utils";
import { getPluginOptions } from "../utils/plugin-utils";
import createSlateEditor from "./createSlateEditor";
const FluentEdit = (_a) => {
    var { chipComponent, comboboxComponent, comboboxRootStyle, comboboxItemComponent } = _a, props = __rest(_a, ["chipComponent", "comboboxComponent", "comboboxRootStyle", "comboboxItemComponent"]);
    const { multiline = true, plugins } = props;
    const [key, setKey] = useState(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => setKey((v) => v + 1), [multiline, JSON.stringify(plugins)]);
    return (_jsx(ComponentProvider, Object.assign({ chipComponent: chipComponent, comboboxComponent: comboboxComponent, comboboxRootStyle: comboboxRootStyle, comboboxItemComponent: comboboxItemComponent }, { children: _jsx(PluginProvider, Object.assign({ plugins: plugins }, { children: _jsx(FluentEditInternal, Object.assign({}, props, { multiline: multiline }), key) })) })));
};
const FluentEditInternal = (props) => {
    const { multiline, autoFocus, placeholder, initialValue: initialTextValue = "", onChange, children, plugins = [] } = props, rest = __rest(props, ["multiline", "autoFocus", "placeholder", "initialValue", "onChange", "children", "plugins"]);
    const overrides = useOverrides();
    const ctx = useFluentEditInternal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const editor = useMemo(() => overrides(createSlateEditor(multiline)), []);
    const _a = useEventHandler(editor), { onPaste, onKeyDown } = _a, eventProps = __rest(_a, ["onPaste", "onKeyDown"]);
    const serializer = useSerialize(plugins);
    const deserializer = useDeserialize(plugins);
    const decorate = useDecorate(editor);
    const renderElement = useCallback((props) => _jsx(ElementRenderer, Object.assign({}, props)), []);
    const renderLeaf = useCallback((props) => _jsx(LeafRenderer, Object.assign({}, props)), []);
    const renderPlaceholder = useCallback((props) => {
        if (editor.children.length === 1 && isParagraph(editor.children[0])) {
            return _jsx(Placeholder, Object.assign({}, props));
        }
        else {
            return _jsx(_Fragment, {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const pluginOptions = useMemo(() => getPluginOptions(plugins, children), [plugins, children]);
    const initialValue = useMemo(() => addRoot(deserializer(initialTextValue, multiline, pluginOptions)), [deserializer, initialTextValue, multiline, pluginOptions]);
    const handleEnterPress = useCallback((event) => {
        if (!multiline) {
            event.preventDefault();
        }
    }, [multiline]);
    const handleKeyDown = useCallback((event) => {
        switch (event.key) {
            case "Enter":
                handleEnterPress(event);
                break;
        }
        onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(event);
    }, [onKeyDown, handleEnterPress]);
    const handleChange = useCallback(() => {
        if (onChange) {
            const text = serializer(editor.children);
            onChange(text);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onChange, serializer]);
    const handlePaste = useCallback((event) => {
        event.preventDefault();
        const text = event.clipboardData.getData("text");
        const nodes = removeRoot(deserializer(text, multiline, pluginOptions));
        Transforms.insertNodes(editor, nodes);
        Transforms.move(editor);
        const editorElem = editorToDomNode(editor);
        editorElem === null || editorElem === void 0 ? void 0 : editorElem.dispatchEvent(new Event("fePaste"));
        onPaste === null || onPaste === void 0 ? void 0 : onPaste(event);
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deserializer, multiline, pluginOptions, onPaste]);
    useLayoutEffect(() => {
        if (ctx) {
            ctx.setEditor(editor);
            ctx.setMultiline(multiline);
            ctx.setPlugins(plugins);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if (autoFocus) {
            focusEditor(editor);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoFocus]);
    return (_jsxs(Slate, Object.assign({ editor: editor, value: initialValue, onChange: handleChange }, { children: [_jsx(Editable, Object.assign({ renderLeaf: renderLeaf, renderElement: renderElement, renderPlaceholder: renderPlaceholder, decorate: decorate, onKeyDown: handleKeyDown, onPaste: handlePaste, placeholder: placeholder }, eventProps, rest, { "data-testid": "rfe" })), children] })));
};
export default FluentEdit;
